import React, { useMemo } from 'react';

import type AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';

import type { GasPurePayload, GasPureScreenEventPayload } from '@atlaskit/analytics-gas-types';
import FabricAnalyticsListeners from '@atlaskit/analytics-listeners/FabricAnalyticsListeners';
import type { AnalyticsWebClient as FabricAnalyticsWebClient } from '@atlaskit/analytics-listeners/types';

import { getAttribution } from './attribution';

const DEFAULT_SOURCE = 'teamworkGraph';

/**
 * Track-event keys (matching `${actionSubject}.${action}`) that should
 * have acquisition attribution merged into their attributes.
 *
 * Track events are usually post-login conversions worth attributing
 * (e.g. `user.loggedIn`). Add to this set as new conversion-style track
 * events are introduced.
 */
const TRACK_EVENTS_WITH_ATTRIBUTION = new Set<string>(['user.loggedIn']);

/**
 * Predicate: should this event automatically inherit the AttributionContext
 * fields (utmSource, trafficSource, gclid, etc.) from `attribution.ts`?
 *
 * Policy:
 *   - All `screen.*` events (page-view-equivalent — always relevant for
 *     funnel slicing).
 *   - All `ui.*` events (CTA clicks, scroll-to-bottom, section reveals,
 *     dropdown interactions — every user interaction is a candidate
 *     funnel step worth attributing).
 *   - Track events listed in TRACK_EVENTS_WITH_ATTRIBUTION (conversions
 *     only — most track events are domain-specific signals that don't
 *     benefit from marketing attribution).
 *   - All `operational.*` events stay lean — they are infra telemetry
 *     (API timing, request success/failure) that marketing dashboards
 *     do not consume.
 *
 * Designed so adding a NEW UI or screen event in the future requires
 * zero changes here: the new event automatically inherits attribution.
 *
 * Because every `ui.*` and `screen.*` event is covered, those call sites must
 * NOT spread `...getAttribution()` into their own attributes: it would read the
 * bag a second time and, while the URL carries UTMs, write it a second time too.
 * The `track.*` events outside the set above are the only ones that carry
 * attribution themselves.
 */
const shouldAttachAttribution = (
	eventType: 'ui' | 'track' | 'screen',
	actionSubject: string,
	action: string,
): boolean => {
	if (eventType === 'screen') return true;
	if (eventType === 'ui') return true;
	if (eventType === 'track') {
		return TRACK_EVENTS_WITH_ATTRIBUTION.has(`${actionSubject}.${action}`);
	}
	return false;
};

/**
 * The attribution attributes to merge into an outgoing event. Re-read per event,
 * so URL-derived fields such as `landingPath` follow the page the event fired on
 * rather than the session's entry point. Any failure (SSR, blocked storage, a
 * parse error in attribution.ts) collapses to `{}` so the event still fires.
 */
const getAttributionAttrs = (): Record<string, unknown> => {
	try {
		return getAttribution();
	} catch {
		return {};
	}
};

/**
 * Adapts @atlassiansox/analytics-web-client to the @atlaskit/analytics-listeners
 * AnalyticsWebClient type. The two packages define AnalyticsWebClient with different
 * method signatures — the SOX version requires `action` and `source` fields that
 * may not be present in the GasPurePayload type. This adapter fills in defaults.
 *
 * Also automatically merges acquisition attribution
 * (`AttributionContext` from `analytics.spec.yaml`) into the events that
 * feed marketing-conversion dashboards. Call-sites do not need to know
 * about attribution — it rides along for free on the events listed above.
 */
const useAnalyticsClientAdapter = (client: AnalyticsWebClient): FabricAnalyticsWebClient =>
	useMemo(
		() => ({
			sendUIEvent: (event: GasPurePayload) => {
				const withAttribution = shouldAttachAttribution(
					'ui',
					event.actionSubject ?? '',
					event.action ?? '',
				)
					? { ...event, attributes: { ...getAttributionAttrs(), ...(event.attributes ?? {}) } }
					: event;
				return client.sendUIEvent({
					...withAttribution,
					action: withAttribution.action || '',
					source: withAttribution.source || DEFAULT_SOURCE,
				});
			},
			sendOperationalEvent: (event: GasPurePayload) =>
				// Operational events are infra telemetry — never attributed.
				client.sendOperationalEvent({
					...event,
					action: event.action || '',
					source: event.source || DEFAULT_SOURCE,
				}),
			sendTrackEvent: (event: GasPurePayload) => {
				const withAttribution = shouldAttachAttribution(
					'track',
					event.actionSubject ?? '',
					event.action ?? '',
				)
					? { ...event, attributes: { ...getAttributionAttrs(), ...(event.attributes ?? {}) } }
					: event;
				return client.sendTrackEvent({
					...withAttribution,
					action: withAttribution.action || '',
					source: withAttribution.source || DEFAULT_SOURCE,
				});
			},
			sendScreenEvent: (event: GasPureScreenEventPayload) => {
				const withAttribution = shouldAttachAttribution('screen', '', '')
					? { ...event, attributes: { ...getAttributionAttrs(), ...(event.attributes ?? {}) } }
					: event;
				return client.sendScreenEvent(withAttribution);
			},
		}),
		[client],
	);

type AnalyticsProviderProps = {
	analyticsClient: AnalyticsWebClient;
	children: React.ReactNode;
};

/**
 * Wraps the app with FabricAnalyticsListeners to capture analytics events
 * fired via @atlaskit/analytics-next and route them to the AnalyticsWebClient.
 *
 * FabricAnalyticsListeners listens on all Fabric/Atlaskit channels (including
 * 'teamworkGraph') and forwards events to the adapted analytics client.
 */
export const AnalyticsProvider = ({
	children,
	analyticsClient,
}: AnalyticsProviderProps): JSX.Element => {
	return (
		<FabricAnalyticsListeners client={useAnalyticsClientAdapter(analyticsClient)}>
			{children}
		</FabricAnalyticsListeners>
	);
};
